import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';

import { combineLatest, map, Observable, of, Subscription, switchMap, zip } from 'rxjs';
import { catchError, filter, take, tap } from 'rxjs/operators';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';

import { CmsImageData, Location } from '@fcom/core-api/interfaces';
import { LocationsService } from '@fcom/locations/services';
import { OrderService } from '@fcom/dapi/api/services';
import { ConfigService, FormPostService } from '@fcom/core/services';
import { Trip } from '@fcom/common/interfaces';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import {
  FinnairBoundItem,
  FinnairCheckInEligibility,
  FinnairCheckInVersion,
  FinnairOrder,
  FinnairPassengerItem,
  RequestInitiator,
} from '@fcom/dapi/api/models';
import { after, before, convertStringDatesToDateObjects, diff, TimeUnit, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx/operators/finShare';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { TagTheme } from '@fcom/ui-components/components/tag';
import { ButtonMode, ButtonTheme, IconButtonTheme } from '@fcom/ui-components/components/buttons';
import { profile } from '@fcom/core/selectors';
import { AppState } from '@fcom/core/interfaces';
import { Profile } from '@fcom/core-api/login';
import { OrderActions, orderData, RedirectActions } from '@fcom/common-booking';
import { RootPaths } from '@fcom/core/constants';
import { NotificationButtonStyle, NotificationTheme } from '@fcom/ui-components/components/notifications';
import { ToasterService } from '@fcom/common/services';
import { CheckInActions } from '@fcom/manage-booking/store';
import { MmbOrderService } from '@fcom/manage-booking-common/services';
import { OffersService } from '@fcom/one-upgrade/api/services/offers.service';
import { PopoverPosition } from '@fcom/ui-components/components/popover';
import { LoaderTheme } from '@fcom/ui-components/components/loader';
import { UpgradeStatus } from '@fcom/one-upgrade/api/models';

import { TripRow } from '../../trips-view/trips-view.component';

@Component({
  selector: 'fin-your-next-trip-widget',
  templateUrl: './your-next-trip-widget.component.html',
  styleUrl: './your-next-trip-widget.component.scss',
})
export class YourNextTripWidgetComponent implements OnInit, OnDestroy {
  protected readonly TagTheme = TagTheme;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly ButtonMode = ButtonMode;
  protected readonly PopoverPosition = PopoverPosition;
  protected readonly IconButtonTheme = IconButtonTheme;
  protected readonly NotificationButtonStyle = NotificationButtonStyle;
  protected readonly LoaderTheme = LoaderTheme;

  @Input({ required: true })
  trip$: Observable<Trip>;

  profile$: Observable<Profile>;
  order$: Observable<FinnairOrder>;
  nextBound$: Observable<FinnairBoundItem>;
  image$: Observable<CmsImageData>;
  daysUntilDeparture$: Observable<number>;
  checkedIn$: Observable<boolean>;
  upgradeAvailable$: Observable<boolean>;
  upgradeImage$: Observable<CmsImageData>;
  subtitle$: Observable<string>;
  showCheckinButton$: Observable<boolean>;
  manageBookingButtonTheme$: Observable<ButtonTheme>;
  locations$: Observable<Location[]>;

  subscription = new Subscription();

  constructor(
    private languageService: LanguageService,
    private locationsService: LocationsService,
    private orderService: OrderService,
    private configService: ConfigService,
    private formPostService: FormPostService,
    private mmbOrderService: MmbOrderService,
    private offersService: OffersService,
    private toasterService: ToasterService,
    private http: HttpClient,
    private router: Router,
    private store$: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profile(), finShare());
    this.order$ = this.trip$.pipe(
      switchMap((nextTrip) => this.loadOrder(nextTrip)),
      tap((order) => this.store$.dispatch(OrderActions.setOrderData({ order }))),
      finShare()
    );

    this.upgradeAvailable$ = this.trip$.pipe(
      switchMap((trip) =>
        this.offersService.getOffersV2({
          recLoc: trip.bookingReference,
          lastName: trip.passengers[0].lastName,
        })
      ),
      catchError(() => of(null)),
      map((offers) =>
        offers?.booking.flights.some((flight) =>
          flight.upgrades.some(({ upgradeStatus }) => upgradeStatus === UpgradeStatus.Available)
        )
      ),
      map(Boolean),
      finShare()
    );

    this.upgradeImage$ = this.http.get<CmsImageData>('https://cms.finnair.com/service/json/v1/ids/fi-en/1983654');

    const now = new Date();

    this.nextBound$ = this.order$.pipe(
      filter(Boolean),
      map((order) =>
        order.bounds.find((bound) =>
          after(convertStringDatesToDateObjects(bound.departure.dateTime), now, TimeUnit.second)
        )
      ),
      finShare()
    );
    this.daysUntilDeparture$ = this.nextBound$.pipe(
      map((bound) => diff(now, convertStringDatesToDateObjects(bound.departure.dateTime), TimeUnit.day))
    );
    const passengerEligibility$ = combineLatest([this.order$, this.nextBound$, this.profile$]).pipe(
      map(([order, nextBound, userProfile]) => {
        const flight = nextBound.itinerary.find(isFlight);
        const passenger = this.getPassenger(order, userProfile);
        const segmentCheckInEligibilities = (order.eligibilities?.checkIn?.find(({ id }) => id === nextBound.id)
          ?.flightTravelerEligibility ?? {})[flight.id];
        return segmentCheckInEligibilities?.find((s) => s.travelerIds.includes(passenger?.id));
      }),
      finShare()
    );
    this.checkedIn$ = passengerEligibility$.pipe(map((passengerEligibility) => passengerEligibility?.isCheckedIn));
    this.showCheckinButton$ = combineLatest([this.order$, this.nextBound$]).pipe(
      map(([order, nextBound]) => {
        const checkInEligibility = order.eligibilities.checkIn.find(({ id }) => id === nextBound.id);
        return checkInEligibility.isAllowedToUseCheckIn;
      })
    );
    this.manageBookingButtonTheme$ = combineLatest([this.showCheckinButton$, this.checkedIn$]).pipe(
      map(([showCheckinButton, checkedIn]) =>
        !showCheckinButton && !checkedIn ? ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE : ButtonTheme.LIGHT_SECONDARY_OUTLINE
      )
    );
    this.subtitle$ = combineLatest([this.order$, this.nextBound$, this.checkedIn$, this.daysUntilDeparture$]).pipe(
      switchMap(([order, nextBound, isCheckedIn, daysUntilDeparture]) =>
        this.getSubtitle(order, nextBound, isCheckedIn, daysUntilDeparture)
      )
    );
    this.locations$ = this.nextBound$.pipe(
      filter(Boolean),
      map((bound) => [bound.departure.locationCode, bound.arrival.locationCode]),
      tap((locationCodes) => this.locationsService.triggerLocationFetchAll(locationCodes)),
      switchMap((locationCodes) =>
        zip(
          this.locationsService.retrieveLocation(locationCodes[0]).pipe(filter(Boolean)),
          this.locationsService.retrieveLocation(locationCodes[1]).pipe(filter(Boolean))
        )
      ),
      finShare()
    );
    this.image$ = this.locations$.pipe(map((locations) => locations[1].picture));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  getSubtitle(
    order: FinnairOrder,
    nextBound: FinnairBoundItem,
    isCheckedIn: boolean,
    daysUntilDeparture: number
  ): Observable<string> {
    const now = new Date();
    const checkinWindow = order.eligibilities.checkIn.find(
      (eligibility) => eligibility.id === nextBound.id
    )?.checkinWindow;
    const checkinStart = checkinWindow?.start ? convertStringDatesToDateObjects(checkinWindow.start) : undefined;
    const checkinEnd = checkinWindow?.end ? convertStringDatesToDateObjects(checkinWindow.end) : undefined;
    if (
      !isCheckedIn &&
      checkinStart &&
      checkinEnd &&
      after(now, checkinStart, TimeUnit.second) &&
      before(now, checkinEnd, TimeUnit.second)
    ) {
      return this.languageService.translate('CORPORATE.corporatePortal.widgets.yourNextTrip.checkInOpen');
    }
    if (isCheckedIn) {
      return this.languageService.translate('CORPORATE.corporatePortal.widgets.yourNextTrip.checkAppForMoreInfo');
    }
    if (daysUntilDeparture > 1) {
      return this.languageService.translate('CORPORATE.corporatePortal.widgets.yourNextTrip.departureInDays', {
        days: daysUntilDeparture,
      });
    }
    if (daysUntilDeparture === 1) {
      return this.languageService.translate('CORPORATE.corporatePortal.widgets.yourNextTrip.departureTomorrow');
    }
    if (daysUntilDeparture === 0) {
      return this.languageService.translate('CORPORATE.corporatePortal.widgets.yourNextTrip.departureToday');
    }
    return this.languageService.translate('CORPORATE.corporatePortal.widgets.yourNextTrip.checkAppForMoreInfo');
  }

  manageBooking(trip: TripRow): void {
    this.store$.dispatch(
      RedirectActions.setPNR({ pnr: { lastName: trip.passengers[0].lastName, recLoc: trip.bookingReference } })
    );
    this.router.navigate([this.languageService.langValue, RootPaths.MANAGE_BOOKING_ROOT]);
  }

  goToCheckin(): void {
    this.subscription.add(
      combineLatest([this.order$, this.nextBound$, this.profile$]).subscribe(([order, nextBound, userProfile]) => {
        const checkInEligibility = order.eligibilities.checkIn.find(({ id }) => id === nextBound.id);
        if (checkInEligibility?.version === FinnairCheckInVersion.LEGACY) {
          this.redirectToLegacy(checkInEligibility);
        } else {
          this.redirectToNextGenCheckIn(order, nextBound, userProfile, checkInEligibility);
        }
      })
    );
  }

  private redirectToNextGenCheckIn(
    order: FinnairOrder,
    nextBound: FinnairBoundItem,
    userProfile: Profile,
    checkInEligibility: FinnairCheckInEligibility
  ): void {
    const eligibleFlightIds = Object.entries(checkInEligibility?.flightTravelerEligibility ?? {}).reduce(
      (all, [flightId, flightEligibility]) => {
        if (flightEligibility.some(({ isAllowedToUseCheckIn }) => isAllowedToUseCheckIn)) {
          all.push(flightId);
        }
        return all;
      },
      []
    );
    this.store$.dispatch(CheckInActions.setBoundId({ boundId: nextBound.id }));
    const passenger = this.getPassenger(order, userProfile);
    this.store$.dispatch(
      CheckInActions.setPassengers({
        passengers: [passenger.id],
      })
    );
    this.mmbOrderService.initializeCheckIn(order.id, order.hash, { flightIds: eligibleFlightIds });
    this.router.navigateByUrl(`${this.languageService.langValue}/manage/check-in/step/passengers`);
  }

  goToBoardingPass(): void {
    this.subscription.add(
      combineLatest([this.order$, this.nextBound$, this.profile$]).subscribe(([order, nextBound, userProfile]) => {
        const checkInEligibility = order.eligibilities.checkIn.find(({ id }) => id === nextBound.id);
        if (checkInEligibility.version === FinnairCheckInVersion.NEXTGEN) {
          this.redirectToNextGenBoardingPass(nextBound, order, userProfile);
        } else if (checkInEligibility.redirect) {
          this.redirectToLegacy(checkInEligibility);
        }
      })
    );
  }

  goToUpgrade(): void {
    this.router.navigate([this.languageService.langValue, RootPaths.MANAGE_BOOKING_ROOT, 'upgrade']);
  }

  goToTravelExtras(trip: Trip): void {
    this.manageBooking(trip);
  }

  redirectToNextGenBoardingPass(bound: FinnairBoundItem, order: FinnairOrder, userProfile: Profile): void {
    this.store$.dispatch(CheckInActions.setBoundId({ boundId: bound.id }));
    const passenger = this.getPassenger(order, userProfile);
    this.store$.dispatch(
      CheckInActions.setPassengers({
        passengers: [passenger.id],
      })
    );
    this.router.navigateByUrl(`${this.languageService.langValue}/manage/check-in/boarding-pass`);
  }

  redirectToLegacy(checkInEligibility: FinnairCheckInEligibility): void {
    this.formPostService.post(
      checkInEligibility.redirect.url,
      checkInEligibility.redirect.params?.reduce((params, param) => {
        params[param.name] = param.value;
        return params;
      }, {}),
      checkInEligibility.redirect.method,
      '_blank'
    );
  }

  copyToClipboard(bookingRef: string): void {
    if (!navigator.clipboard) {
      this.showToasterMessage(
        'CORPORATE.corporatePortal.widgets.yourNextTrip.bookingRefCopyFailed',
        NotificationTheme.ALERT
      );
      return;
    }
    navigator.clipboard.writeText(bookingRef).then(
      () => {
        this.showToasterMessage(
          'CORPORATE.corporatePortal.widgets.yourNextTrip.bookingRefCopied',
          NotificationTheme.INFO
        );
      },
      () => {
        this.showToasterMessage(
          'CORPORATE.corporatePortal.widgets.yourNextTrip.bookingRefCopyFailed',
          NotificationTheme.ALERT
        );
      }
    );
  }

  private showToasterMessage(
    contentKey: string,
    theme: Exclude<NotificationTheme, NotificationTheme.TRANSPARENT>
  ): void {
    this.toasterService.addMessageToQueue({
      id: 'CLIPBOARD_COPY',
      contentKey,
      theme,
      autoClose: true,
    });
  }

  private getPassenger(order: FinnairOrder, userProfile: Profile): FinnairPassengerItem {
    return order.passengers.find(
      (p) =>
        p.firstName.toLowerCase() === userProfile.firstname.toLowerCase() &&
        p.lastName.toLowerCase() === userProfile.lastname.toLowerCase()
    );
  }

  private loadOrder(trip: Trip) {
    return this.store$.pipe(
      orderData(true),
      switchMap((order) => (order?.id === trip.bookingReference ? of(order) : this.loadOrderFromApi(trip))),
      take(1)
    );
  }

  private loadOrderFromApi(trip: Trip) {
    const params = new HttpParams().set('includeServiceCatalog', true);
    return this.orderService
      .getOrderByPnrAndLastName$Response(this.configService.cfg.orderUrl, {
        body: {
          id: trip.bookingReference.toUpperCase(),
          lastName: trip.passengers[0].lastName,
          locale: this.languageService.localeValue,
          requestInitiator: RequestInitiator.UNKNOWN,
          firstName: trip.passengers[0].firstName,
        },
      })
      .pipe(
        map((response) => response.headers.get('Location')),
        switchMap((orderUrl) => this.http.get<FinnairOrder>(orderUrl, { params }))
      );
  }
}
